'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { DEBUG_ANNOUNCEMENT } from '@/components/discover/announcement-dialog';
import { fetchUnreadAnnouncement, markAnnouncementRead } from '@/utils/util_announcement';
import type { Announcement } from '@/utils/util_types';

export function useAnnouncement(active: boolean, ready: boolean) {
    const [announcement, setAnnouncement] = useState<Announcement | null>(null);
    const [preview, setPreview] = useState(false);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const previewRef = useRef(false);

    const openPreview = useCallback(() => {
        if (process.env.NODE_ENV === 'production') return;
        previewRef.current = true;
        setPreview(true);
    }, []);

    useEffect(() => {
        if (!active) {
            setAnnouncement(null);
            setPreview(false);
            previewRef.current = false;
            return;
        }
        if (!ready) return;
        let cancelled = false;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);
        if (process.env.NODE_ENV !== 'production' && new URLSearchParams(window.location.search).get('debugAnnouncement') === '1') openPreview();
        void fetchUnreadAnnouncement(controller.signal).then((value) => {
            if (!cancelled && !controller.signal.aborted) setAnnouncement(value);
        }).catch(() => { /* 公告不可用时仍可浏览发现页。 */ }).finally(() => {
            clearTimeout(timeout);
        });
        return () => {
            cancelled = true;
            controller.abort();
            clearTimeout(timeout);
        };
    }, [active, ready, openPreview]);

    const confirm = useCallback(async () => {
        if (savingRef.current) return;
        if (previewRef.current) {
            previewRef.current = false;
            setPreview(false);
            return;
        }
        if (!announcement) return;
        savingRef.current = true;
        setSaving(true);
        try {
            await markAnnouncementRead(announcement.id);
            setAnnouncement(null);
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    }, [announcement]);

    return {
        announcement: active && ready ? (preview ? DEBUG_ANNOUNCEMENT : announcement) : null,
        preview,
        saving,
        openPreview,
        confirm: () => { void confirm(); },
    };
}
